import React, { useState, useEffect } from "react";
import axios from "axios";
import AccountTabs from "./AccountTabs";
import { Icon } from "./Icon";

interface AccountProps {
  user: {
    id: string;
    username: string;
    displayName?: string;
    display_name?: string;
    profileImageUrl?: string;
    profile_image_url?: string;
    userType: "user" | "premium" | "vip" | "admin";
    email?: string;
    timezone?: string;
    approved?: boolean;
  };
  authToken: string;
}

interface SubscriptionInfo {
  status: "active" | "canceled" | "past_due" | null;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
}

const Account: React.FC<AccountProps> = ({ user, authToken }) => {
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionInfo>({
    status: null,
  });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (user.userType === "premium") {
      fetchSubscriptionInfo();
    }
  }, [user.userType]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 860);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await axios.get("/api/subscription/status", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setSubscription(response.data);
    } catch (error) {
      console.error("Error fetching subscription info:", error);
    }
  };

  const handleUpgradeClick = async () => {
    setProcessingPayment(true);
    try {
      const response = await axios.post(
        "/api/subscription/create-checkout-session",
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      alert("Failed to start upgrade process. Please try again.");
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (
      !window.confirm(
        "Are you sure you want to cancel your subscription? You will keep access until the end of your current billing period.",
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        "/api/subscription/cancel",
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      await fetchSubscriptionInfo();
      alert(
        "Your subscription has been canceled. You will retain access until the end of your billing period.",
      );
    } catch (error) {
      console.error("Error canceling subscription:", error);
      alert("Failed to cancel subscription. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setLoading(true);
    try {
      await axios.post(
        "/api/subscription/reactivate",
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      await fetchSubscriptionInfo();
      alert(
        "Your subscription has been reactivated! You will continue to be billed monthly.",
      );
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      alert("Failed to reactivate subscription. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getUserTypeDisplay = () => {
    switch (user.userType) {
      case "admin":
        return { text: "Administrator", color: "#EF4444" };
      case "premium":
        return { text: "Premium", color: "#9146FF" };
      default:
        return { text: "Free", color: "#6B7280" };
    }
  };

  const userTypeInfo = getUserTypeDisplay();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "center",
        gap: "20px",
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "0 20px",
      }}
    >
      <div
        style={{
          flex: "1",
          maxWidth: isMobile ? "100%" : "800px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{ fontSize: "20px", margin: "0 0 24px 0", color: "#e2e8f0" }}
        >
          Account Settings
        </h3>

        {/* User Info Card */}
        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "24px",
            borderRadius: "8px",
            marginBottom: "20px",
            border: "1px solid #4a5568",
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
              gap: "24px",
            }}
          >
            <img
              src={user.profileImageUrl || user.profile_image_url}
              alt={user.displayName || user.display_name}
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
            <div style={{ flex: 1 }}>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "24px",
                  color: "#e2e8f0",
                }}
              >
                {user.displayName || user.display_name}
              </h4>
              <p
                style={{
                  margin: "0 0 8px 0",
                  color: "#cbd5e0",
                  fontSize: "16px",
                }}
              >
                @{user.username}
              </p>
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <span
                  style={{
                    padding: "4px 12px",
                    borderRadius: "16px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    backgroundColor: userTypeInfo.color + "20",
                    color: userTypeInfo.color,
                  }}
                >
                  {userTypeInfo.text}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Management - Only for premium users */}
        {user.userType === "premium" ? (
          // Premium User - Subscription Management
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "24px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              boxSizing: "border-box",
              width: "100%",
              marginTop: "20px",
            }}
          >
            <h4
              style={{
                margin: "0 0 16px 0",
                color: "#9146FF",
                fontSize: "18px",
              }}
            >
              <Icon
                name="sparkles"
                size={18}
                style={{ verticalAlign: "-3px", marginRight: "8px" }}
              />
              Premium Subscription
            </h4>

            {subscription.status ? (
              subscription.status === "active" ? (
                <>
                  <div style={{ marginBottom: "20px" }}>
                    <p
                      style={{
                        color: "#10B981",
                        fontSize: "16px",
                        marginBottom: "8px",
                      }}
                    >
                      <Icon
                        name="check"
                        size={16}
                        color="#10B981"
                        style={{ verticalAlign: "-3px", marginRight: "8px" }}
                      />
                      Your subscription is active
                    </p>
                    <div style={{ fontSize: "14px", color: "#cbd5e0" }}>
                      {subscription.cancelAtPeriodEnd ? (
                        <>
                          <p style={{ margin: "4px 0" }}>
                            <strong>Status:</strong> Canceled (will not renew)
                          </p>
                          {subscription.currentPeriodEnd && (
                            <p style={{ margin: "4px 0" }}>
                              <strong>Access until:</strong>{" "}
                              {formatDate(subscription.currentPeriodEnd)}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          {subscription.currentPeriodEnd && (
                            <p style={{ margin: "4px 0" }}>
                              <strong>Next billing date:</strong>{" "}
                              {formatDate(subscription.currentPeriodEnd)}
                            </p>
                          )}
                          <p style={{ margin: "4px 0" }}>
                            <strong>Amount:</strong> $4.99/month
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      backgroundColor: "#1a202c",
                      padding: "16px",
                      borderRadius: "6px",
                      marginBottom: "20px",
                    }}
                  >
                    <h5
                      style={{
                        color: "#e2e8f0",
                        marginTop: 0,
                        marginBottom: "12px",
                        fontSize: "15px",
                      }}
                    >
                      <Icon
                        name="party-popper"
                        size={18}
                        style={{ verticalAlign: "-3px", marginRight: "8px" }}
                      />
                      Your Premium Benefits:
                    </h5>
                    <ul
                      style={{
                        color: "#cbd5e0",
                        margin: 0,
                        paddingLeft: "20px",
                        fontSize: "13px",
                        lineHeight: "1.8",
                      }}
                    >
                      <li>
                        All premium personalities unlocked (DJ, Musician, Synth
                        Nerd, Gamer, etc.)
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#a0aec0",
                            marginTop: "4px",
                            marginLeft: "0",
                            fontStyle: "italic",
                          }}
                        >
                          Choose from dozens of HAL personalities, make him an
                          expert on your stream topic.
                        </div>
                      </li>
                      <li>
                        Custom context configuration for unique bot behavior
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#a0aec0",
                            marginTop: "4px",
                            marginLeft: "0",
                            fontStyle: "italic",
                          }}
                        >
                          Make your HAL truly your own - customize his
                          personality, teach him anything you want, how to
                          behave, how to talk, style, etc.
                        </div>
                      </li>
                      <li>
                        Daily conversation summaries with insights
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#a0aec0",
                            marginTop: "4px",
                            marginLeft: "0",
                            fontStyle: "italic",
                          }}
                        >
                          Understand your audience, their interests, mood,
                          topics, trends over time.
                        </div>
                      </li>
                      <li>
                        Real-time data management for dynamic context with API
                        support
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#a0aec0",
                            marginTop: "4px",
                            marginLeft: "0",
                            fontStyle: "italic",
                          }}
                        >
                          Ingest real-time data into HAL - current song, BPM,
                          game you&apos;re playing, weather, anything you want.
                          HAL will discuss these topics informedly with your
                          audience. Full API support included.
                        </div>
                      </li>
                    </ul>
                  </div>

                  {subscription.cancelAtPeriodEnd ? (
                    <>
                      <div
                        style={{
                          backgroundColor: "#FEF3C7",
                          color: "#92400E",
                          padding: "12px 16px",
                          borderRadius: "6px",
                          marginBottom: "20px",
                          fontSize: "14px",
                        }}
                      >
                        <strong>
                          <Icon
                            name="alert-triangle"
                            size={16}
                            style={{
                              verticalAlign: "-3px",
                              marginRight: "8px",
                            }}
                          />
                          Subscription Canceled
                        </strong>
                        <p style={{ margin: "8px 0 0 0" }}>
                          Your subscription will not renew. You&apos;ll continue
                          to have premium access until{" "}
                          {formatDate(subscription.currentPeriodEnd!)}.
                        </p>
                      </div>
                      <button
                        onClick={handleReactivateSubscription}
                        disabled={loading}
                        style={{
                          width: "100%",
                          padding: "12px 24px",
                          backgroundColor: loading ? "#4a5568" : "#10B981",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          fontSize: "16px",
                          fontWeight: "bold",
                          cursor: loading ? "not-allowed" : "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        {loading ? (
                          "Processing..."
                        ) : (
                          <>
                            <Icon
                              name="refresh-cw"
                              size={16}
                              style={{
                                verticalAlign: "-3px",
                                marginRight: "8px",
                              }}
                            />
                            Reactivate Subscription
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleCancelSubscription}
                      disabled={loading}
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "transparent",
                        color: "#EF4444",
                        border: "1px solid #EF4444",
                        borderRadius: "6px",
                        fontSize: "14px",
                        cursor: loading ? "not-allowed" : "pointer",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        if (!loading) {
                          e.currentTarget.style.backgroundColor = "#EF4444";
                          e.currentTarget.style.color = "white";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#EF4444";
                      }}
                    >
                      {loading ? "Processing..." : "Cancel Subscription"}
                    </button>
                  )}
                </>
              ) : (
                <div style={{ color: "#cbd5e0" }}>
                  <p>Subscription status: {subscription.status}</p>
                  <p
                    style={{
                      fontSize: "14px",
                      marginTop: "8px",
                      color: "#718096",
                    }}
                  >
                    Please contact support if you need assistance with your
                    subscription.
                  </p>
                </div>
              )
            ) : (
              <div style={{ color: "#cbd5e0" }}>
                <p>Loading subscription information...</p>
                <p
                  style={{
                    fontSize: "14px",
                    marginTop: "8px",
                    color: "#718096",
                  }}
                >
                  If you just subscribed, please refresh the page.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    marginTop: "12px",
                    padding: "8px 16px",
                    backgroundColor: "#4a5568",
                    color: "#e2e8f0",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Refresh Page
                </button>
              </div>
            )}
          </div>
        ) : null}

        {/* Tabbed Content */}
        <AccountTabs user={user} authToken={authToken} />
      </div>

      {/* Account Info Panel */}
      <div
        style={{
          width: isMobile ? "100%" : "350px",
          maxWidth: isMobile ? "800px" : "350px",
          backgroundColor: "rgba(45, 55, 72, 0.95)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          borderRadius: "8px",
          padding: "20px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)",
          height: "fit-content",
          position: isMobile ? "static" : "sticky",
          top: "80px",
          order: isMobile ? 2 : 0,
          boxSizing: "border-box",
        }}
      >
        <h3
          style={{
            color: "#e2e8f0",
            marginTop: 0,
            marginBottom: "20px",
            fontSize: "18px",
          }}
        >
          <Icon
            name="user"
            size={18}
            style={{ verticalAlign: "-3px", marginRight: "8px" }}
          />
          Account Information
        </h3>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="gamepad-2"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Twitch Connection
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            Your account is linked to your Twitch profile. HAL uses this to join
            your channel.
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="gem"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Account Types
          </h4>
          <ul
            style={{
              color: "#cbd5e0",
              fontSize: "12px",
              margin: 0,
              paddingLeft: "18px",
            }}
          >
            <li>
              <strong>Free</strong> - Basic personalities & features
            </li>
            <li>
              <strong>Premium</strong> - All personalities & custom context
            </li>
          </ul>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="credit-card"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Billing
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            Subscriptions are billed monthly via Stripe. You can cancel anytime
            and keep access until the end of your billing period.
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="key"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            API Access
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            Generate an API token to integrate HAL8580 with external
            applications and services.
          </p>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <h4
            style={{ color: "#9146FF", marginBottom: "8px", fontSize: "14px" }}
          >
            <Icon
              name="globe"
              size={16}
              style={{ verticalAlign: "-3px", marginRight: "8px" }}
            />
            Timezone
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              fontSize: "13px",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            Your timezone is used for scheduling time-based triggers and
            displaying accurate timestamps.
          </p>
        </div>

        {(user.userType === "premium" ||
          user.userType === "vip" ||
          user.userType === "admin") && (
          <div style={{ marginBottom: "20px" }}>
            <h4
              style={{
                color: "#9146FF",
                marginBottom: "8px",
                fontSize: "14px",
              }}
            >
              <Icon
                name="train"
                size={16}
                style={{ verticalAlign: "-3px", marginRight: "8px" }}
              />
              RaidRunner Integration
            </h4>
            <p
              style={{
                color: "#cbd5e0",
                fontSize: "13px",
                margin: 0,
                lineHeight: "1.5",
              }}
            >
              Platform for organizing Twitch raid trains. Track current raid
              train events in real-time.
            </p>
          </div>
        )}

        <div
          style={{
            backgroundColor: "#1a202c",
            padding: "12px",
            borderRadius: "6px",
            marginTop: "15px",
            border: "1px solid #4a5568",
          }}
        >
          <p
            style={{
              color: "#9146FF",
              fontSize: "12px",
              margin: "0 0 6px 0",
              fontWeight: "bold",
            }}
          >
            <Icon
              name="lightbulb"
              size={14}
              style={{ verticalAlign: "-2px", marginRight: "6px" }}
            />
            Need Help?
          </p>
          <p style={{ color: "#a0aec0", fontSize: "11px", margin: 0 }}>
            <a
              href="/support"
              style={{ color: "#9146FF", textDecoration: "none" }}
            >
              Contact support
            </a>{" "}
            for billing or account issues.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Account;
